import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { toast } from "sonner";
import { generateAiResponse } from "@/services/ai";
import { AI_MODELS } from "./mockData";

const SonasContext = createContext(null);

export const SonasProvider = ({ children }) => {
  const { user, profile, refreshProfile } = useAuth();
  
  const [conversations, setConversations] = useState({});
  const [folders, setFolders] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [isPossessionsOpen, setIsPossessionsOpen] = useState(false);
  const [isAffiliateOpen, setIsAffiliateOpen] = useState(false);
  const [isWalkthroughOpen, setIsWalkthroughOpen] = useState(false);
  const [isProTutorial, setIsProTutorial] = useState(false);
  
  const [isThinking, setIsThinking] = useState(false);
  const [isProjectConnected, setIsProjectConnected] = useState(false);
  const [accumulatedText, setAccumulatedText] = useState("");

  // Enforcement
  const isChatRestricted = profile?.is_chat_restricted === true;
  const isImageRestricted = profile?.is_image_upload_restricted === true;
  const isPossessionsRestricted = profile?.is_possessions_restricted === true;
  const isProjectRestricted = profile?.is_project_restricted === true;
  const isAccountRestricted = profile?.is_account_edit_restricted === true;
  const isTokenFrozen = profile?.is_token_usage_frozen === true;
  const isPluginRestricted = profile?.is_plugin_restricted === true;

  // Initialize walkthrough if user hasn't seen it
  useEffect(() => {
    if (profile && profile.has_seen_tutorial === false) {
      setIsWalkthroughOpen(true);
    }
  }, [profile]);

  const completeWalkthrough = async () => {
    setIsWalkthroughOpen(false);
    if (!user) return;
    try {
      await supabase.from('profiles').update({ has_seen_tutorial: true }).eq('id', user.id);
      refreshProfile();
    } catch (err) { console.error(err); }
  };

  const fetchFolders = useCallback(async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('project_folders')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFolders(data || []);
    } catch (err) { console.error(err); }
  }, [user]);

  const createFolder = async (name) => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('project_folders')
        .insert([{ name, user_id: user.id }])
        .select().single();
      
      if (error) throw error;
      setFolders(prev => [data, ...prev]);
      toast.success("Folder created.");
      setIsCreateFolderOpen(false);
      return data.id;
    } catch (err) { toast.error("Failed to create folder."); }
  };

  const onSend = useCallback(async (text, options = {}) => {
    if (!activeConversationId || !user) return;
    const trimmed = (text || "").trim();
    if (!trimmed && !options.image) return;

    setIsThinking(true);
    setAccumulatedText("");
    
    try {
      await supabase.from('messages').insert([{
        project_id: activeConversationId, user_id: user.id, role: 'user', content: trimmed, image_url: options.image || null
      }]);

      await fetchMessages(activeConversationId);

      const activeConv = conversations[activeConversationId];
      const history = (activeConv?.messages || []).map(m => `${m.role}: ${m.text}`).join('\n');
      
      const aiFinalResponse = await generateAiResponse({
        prompt: trimmed, 
        context: history, 
        model: options?.model ?? "nova-fast", 
        image: options.image,
        onChunk: (chunk) => setAccumulatedText(chunk)
      });

      await supabase.from('messages').insert([{
        project_id: activeConversationId, user_id: user.id, role: 'assistant', content: aiFinalResponse || "..."
      }]);

      refreshProfile();
      await fetchMessages(activeConversationId);
    } catch (err) {
      toast.error(`Sync Error: ${err.message}`);
    } finally {
      setIsThinking(false);
      setAccumulatedText("");
    }
  }, [activeConversationId, user, conversations, refreshProfile]);

  const fetchMessages = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const formattedMessages = (data ?? []).map(m => ({
        id: m.id,
        role: m.role,
        text: m.content || "",
        imageUrl: m.image_url,
        time: new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        tasks: m.tasks || []
      }));

      setConversations(prev => ({ ...prev, [projectId]: { ...(prev[projectId] || {}), messages: formattedMessages } }));
    } catch (err) { console.error(err); }
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!user) { setIsLoading(false); return; }
    try {
      const { data, error } = await supabase.from('projects').select('*').order('updated_at', { ascending: false });
      if (error) throw error;
      
      setConversations(prev => {
        const next = { ...prev };
        (data ?? []).forEach(p => {
          next[p.id] = {
            ...(next[p.id] || {}),
            id: p.id,
            name: p.title,
            date: p.updated_at ? new Date(p.updated_at).toLocaleDateString() : 'Just now',
            messages: next[p.id]?.messages || [],
            share_id: p.share_id,
            folder_id: p.folder_id
          };
        });
        return next;
      });
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  }, [user]);

  const deleteProject = useCallback(async (projectId) => {
    try {
      const { error } = await supabase.from('projects').delete().eq('id', projectId);
      if (error) throw error;
      setConversations(prev => {
        const next = { ...prev };
        delete next[projectId];
        return next;
      });
      toast.success("Project purged.");
      return true;
    } catch (err) { toast.error("Failed to delete project."); return false; }
  }, []);

  const createConversation = useCallback(async (customName) => {
    if (!user || !profile || isProjectRestricted) return;
    const currentCount = Object.keys(conversations ?? {}).length;
    const limit = profile.custom_project_limit || (profile.is_pro ? 10 : 3);
    if (currentCount >= limit) { toast.error("Limit reached."); return; }
    
    const shareId = Math.random().toString(36).substring(2, 10);
    
    try {
      const { data, error } = await supabase.from('projects').insert([{ 
        user_id: user.id, title: customName || "New Project", status: 'Draft', share_id: shareId
      }]).select().single();
      
      if (error) throw error;
      setConversations(prev => ({ ...prev, [data.id]: { id: data.id, name: data.title, date: 'Just now', messages: [], share_id: data.share_id } }));
      setActiveConversationId(data.id);
      setIsCreateDialogOpen(false);
      return data.id;
    } catch (err) { toast.error("Creation failed."); }
  }, [user, profile, conversations, isProjectRestricted]);

  useEffect(() => { 
    if (user) { fetchProjects(); fetchFolders(); } 
  }, [user, fetchProjects, fetchFolders]);

  return (
    <SonasContext.Provider value={{
      isProjectConnected, setIsProjectConnected,
      conversations, setConversations,
      folders, createFolder, fetchFolders,
      activeConversationId, setActiveConversationId,
      createConversation, fetchProjects, fetchMessages, deleteProject,
      onSend, accumulatedText,
      isInstallModalOpen, setIsInstallModalOpen,
      isStoreOpen, setIsStoreOpen,
      isSettingsOpen, setIsSettingsOpen,
      isCreateDialogOpen, setIsCreateDialogOpen,
      isCreateFolderOpen, setIsCreateFolderOpen,
      isPossessionsOpen, setIsPossessionsOpen,
      isAffiliateOpen, setIsAffiliateOpen,
      isWalkthroughOpen, setIsWalkthroughOpen,
      completeWalkthrough,
      isProTutorial, setIsProTutorial,
      isThinking, setIsThinking,
      isChatRestricted, isImageRestricted, isPossessionsRestricted, isProjectRestricted, isAccountRestricted, isTokenFrozen, isPluginRestricted,
      tokens: profile?.balance ?? 0.00,
      isLoading
    }}>
      {children}
    </SonasContext.Provider>
  );
};

export const useSonas = () => useContext(SonasContext);